import React, { useMemo, useState } from "react";
import {
  FaEnvelope,
  FaHeadset,
  FaDiscord,
  FaYoutube,
  FaLinkedin,
  FaRobot,
  FaComments,
  FaBookOpen
} from "react-icons/fa";
import { DISCORD_URL } from "../data/siteCatalog";
import "../style/ContactSection.css";

const initialForm = {
  name: "",
  email: "",
  reason: "supporto giochi",
  message: ""
};

const contactChannels = [
  {
    icon: FaHeadset,
    title: "Supporto",
    description: "Account, launcher, installazione, licenze e problemi con i giochi Kyros.",
    action: { label: "support@kyrosgames.com", href: "mailto:support@kyrosgames.com" }
  },
  {
    icon: FaEnvelope,
    title: "Stampa e contenuti",
    description: "Richieste media, video, articoli e materiali sui giochi autoprodotti Kyros.",
    action: { label: "business@kyrosgames.com", href: "mailto:business@kyrosgames.com" }
  },
  {
    icon: FaDiscord,
    title: "Community",
    description: "Aggiornamenti, playtest dei nostri giochi e contatto diretto con il team Kyros.",
    action: { label: "Discord Kyros", href: DISCORD_URL }
  }
];

const supportPlans = [
  {
    title: "Problema tecnico",
    items: ["Gioco interessato", "PC e versione Windows", "Cosa succede"],
    accent: "1"
  },
  {
    title: "Account e Store",
    items: ["Email account Kyros", "Ordine o licenza", "Pagina o errore visto"],
    accent: "2"
  },
  {
    title: "Stampa e contenuti",
    items: ["Canale o testata", "Gioco interessato", "Richiesta specifica"],
    accent: "3"
  }
];

const socialLinks = [
  { icon: FaDiscord, label: "Discord", href: DISCORD_URL },
  { icon: FaYoutube, label: "YouTube", href: "https://www.youtube.com/@KyrosGamesOfficial" },
  { icon: FaLinkedin, label: "LinkedIn", href: "https://linkedin.com/company/kyrosgames" }
];

const supportHighlights = [
  {
    icon: FaRobot,
    title: "Launcher",
    description: "Download, installazione, aggiornamenti o accesso al launcher.",
    footer: "Scrivi cosa succede e su che PC."
  },
  {
    icon: FaComments,
    title: "Account",
    description: "Login, libreria, licenza e giochi collegati al profilo Kyros.",
    footer: "Usa la stessa email dell'account."
  },
  {
    icon: FaBookOpen,
    title: "Giochi Kyros",
    description: "Domande su Zombie Crushers, WarFront o Ghost Hunter.",
    footer: "Indica il gioco nel messaggio."
  }
];

export default function ContactSection() {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const mailtoHref = useMemo(() => {
    const subject = encodeURIComponent(`Kyros - ${form.reason}`);
    const body = encodeURIComponent(
      `Nome: ${form.name}\nEmail: ${form.email}\nMotivo: ${form.reason}\n\nMessaggio:\n${form.message}`
    );
    return `mailto:support@kyrosgames.com?subject=${subject}&body=${body}`;
  }, [form]);

  const updateField = event => {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: value }));
    setError("");
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Compila nome, email e messaggio.");
      setSubmitted(false);
      return;
    }
    setSubmitted(true);
  };

  return (
    <section id="contatti" className="contact-section">
      <div className="contact-section__inner">
        <header className="contact-hero">
          <span className="contact-hero__eyebrow">Contatti</span>
          <h1>Parla con Kyros</h1>
          <p>
            Scrivici per problemi con account, launcher, Store o giochi. Per stampa e video,
            indicaci il titolo Kyros di cui vuoi parlare e che materiale ti serve.
          </p>
          <div className="contact-hero__details">
            {supportHighlights.map(item => {
              const Icon = item.icon;
              return (
                <article key={item.title}>
                  <Icon aria-hidden="true" />
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <small>{item.footer}</small>
                  </div>
                </article>
              );
            })}
          </div>
        </header>

        <section className="contact-form-panel" aria-label="Modulo contatti">
          <div className="contact-form-panel__copy">
            <span>Modulo</span>
            <h2>Prepara una email</h2>
            <p>
              Compila i campi e prepara una bozza email: la controlli nel tuo client prima di inviarla.
            </p>
          </div>
          <form className="contact-form-panel__form" onSubmit={handleSubmit} noValidate>
            <label>
              Nome
              <input name="name" type="text" value={form.name} onChange={updateField} autoComplete="name" />
            </label>
            <label>
              Email
              <input name="email" type="email" value={form.email} onChange={updateField} autoComplete="email" />
            </label>
            <label>
              Motivo
              <select name="reason" value={form.reason} onChange={updateField}>
                <option value="supporto giochi">Supporto giochi</option>
                <option value="supporto launcher">Supporto launcher</option>
                <option value="account e libreria">Account e libreria</option>
                <option value="acquisti e rimborsi">Acquisti e rimborsi</option>
                <option value="stampa e contenuti">Stampa e contenuti</option>
                <option value="community">Community</option>
              </select>
            </label>
            <label>
              Messaggio
              <textarea name="message" value={form.message} onChange={updateField} rows="6" />
            </label>
            {error ? <p className="contact-form-panel__error">{error}</p> : null}
            {submitted ? (
              <div className="contact-form-panel__success">
                <strong>Bozza pronta.</strong>
                <a href={mailtoHref}>Apri email</a>
              </div>
            ) : null}
            <button type="submit">Prepara email</button>
          </form>
        </section>

        <section className="contact-channels" aria-label="Canali di contatto">
          <div className="contact-channels__grid">
            {contactChannels.map(channel => {
              const Icon = channel.icon;
              const external = channel.action.href.startsWith("http");
              return (
                <article key={channel.title} className="contact-channel-card">
                  <div className="contact-channel-card__icon"><Icon aria-hidden="true" /></div>
                  <h3>{channel.title}</h3>
                  <p>{channel.description}</p>
                  <a
                    className="contact-channel-card__link"
                    href={channel.action.href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noreferrer" : undefined}
                  >
                    {channel.action.label}
                  </a>
                </article>
              );
            })}
          </div>
        </section>

        <section className="contact-support" aria-label="Come rispondiamo">
          <h2>Cosa ci serve per rispondere bene</h2>
          <div className="contact-support__grid">
            {supportPlans.map(plan => (
              <article key={plan.title} className="contact-support-card">
                <span className="contact-support-card__badge">{plan.accent}</span>
                <h3>{plan.title}</h3>
                <ul>{plan.items.map(item => <li key={item}>{item}</li>)}</ul>
              </article>
            ))}
          </div>
        </section>

        <section className="contact-social" id="discord" aria-label="Canali social">
          <div className="contact-social__card">
            <h2>Canali Kyros</h2>
            <p>Per aggiornamenti rapidi su giochi, Store, launcher e playtest Kyros.</p>
            <div className="contact-social__links">
              {socialLinks.map(link => {
                const Icon = link.icon;
                return (
                  <a key={link.label} href={link.href} target="_blank" rel="noreferrer" className="contact-social__link">
                    <Icon aria-hidden="true" />
                    <span>{link.label}</span>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </section>
  );
}
